import { FeatureName } from "#shared/featureAccessContract.ts";
import { FEATURE_ACCESS_SEGMENT, FeatureSegments } from "./featureSegments.ts";

export type FeatureId = keyof typeof FeatureSegments;

const featurePath = (id: FeatureId) => `/settings${FeatureSegments[id]}`;

export const PERFORMANCE_REVIEWS_ACCESS_PATH = `${featurePath("performance-reviews")}${FEATURE_ACCESS_SEGMENT}`;

export const GOOD_VIBES_PATH = featurePath("good-vibes");

export const GOOD_VIBES_COLLECTIONS_PATH = `${GOOD_VIBES_PATH}/collections`;

export const GOALS_ACCESS_PATH = `${featurePath("goals")}${FEATURE_ACCESS_SEGMENT}`;

export interface FeatureCatalogueEntry {
    id: FeatureId;
    featureName: FeatureName;
    rowPath: string;
    accessPath: string;
}

export const FEATURE_CATALOGUE: readonly FeatureCatalogueEntry[] = [
    { id: "compensation", featureName: FeatureName.COMPENSATION, rowPath: featurePath("compensation"), accessPath: `${featurePath("compensation")}${FEATURE_ACCESS_SEGMENT}` },
    { id: "engagement", featureName: FeatureName.ENGAGEMENT, rowPath: featurePath("engagement"), accessPath: `${featurePath("engagement")}${FEATURE_ACCESS_SEGMENT}` },
    { id: "goals", featureName: FeatureName.GOALS, rowPath: featurePath("goals"), accessPath: featurePath("goals") },
    { id: "good-vibes", featureName: FeatureName.RECOGNITION, rowPath: featurePath("good-vibes"), accessPath: `${featurePath("good-vibes")}${FEATURE_ACCESS_SEGMENT}` },
    { id: "meetings", featureName: FeatureName.MEETINGS, rowPath: featurePath("meetings"), accessPath: featurePath("meetings") },
    { id: "org-chart", featureName: FeatureName.ORG_CHART, rowPath: featurePath("org-chart"), accessPath: featurePath("org-chart") },
    { id: "performance-reviews", featureName: FeatureName.PERFORMANCE_REVIEWS, rowPath: PERFORMANCE_REVIEWS_ACCESS_PATH, accessPath: PERFORMANCE_REVIEWS_ACCESS_PATH },
    { id: "plans", featureName: FeatureName.PLANS, rowPath: featurePath("plans"), accessPath: featurePath("plans") }
];

const CATALOGUE_WITHOUT_ORG_CHART = FEATURE_CATALOGUE.filter(entry => entry.id !== "org-chart");

function withGoalsAccessTab(catalogue: readonly FeatureCatalogueEntry[]): readonly FeatureCatalogueEntry[] {
    return catalogue.map(entry => (entry.id === "goals" ? { ...entry, accessPath: GOALS_ACCESS_PATH } : entry));
}

const CATALOGUE_WITH_GOALS_ACCESS_TAB = withGoalsAccessTab(FEATURE_CATALOGUE);

const CATALOGUE_WITH_GOALS_ACCESS_TAB_WITHOUT_ORG_CHART = withGoalsAccessTab(CATALOGUE_WITHOUT_ORG_CHART);

export interface FeatureCatalogueOptions {
    isGoalSettingsEnabled?: boolean;
    isGoodVibesSettingsEnabled?: boolean;
}

export function getFeatureCatalogue(isOrgChartEnabled: boolean, { isGoalSettingsEnabled = false, isGoodVibesSettingsEnabled = false }: FeatureCatalogueOptions = {}): readonly FeatureCatalogueEntry[] {
    const catalogue = isGoalSettingsEnabled ? (isOrgChartEnabled ? CATALOGUE_WITH_GOALS_ACCESS_TAB : CATALOGUE_WITH_GOALS_ACCESS_TAB_WITHOUT_ORG_CHART) : isOrgChartEnabled ? FEATURE_CATALOGUE : CATALOGUE_WITHOUT_ORG_CHART;

    return isGoodVibesSettingsEnabled ? withGoodVibesCollectionsRow(catalogue) : catalogue;
}

const goodVibesCollectionsCatalogues = new WeakMap<readonly FeatureCatalogueEntry[], readonly FeatureCatalogueEntry[]>();

function withGoodVibesCollectionsRow(catalogue: readonly FeatureCatalogueEntry[]): readonly FeatureCatalogueEntry[] {
    const cached = goodVibesCollectionsCatalogues.get(catalogue);

    if (cached) {
        return cached;
    }

    const rows = catalogue.map(entry => (entry.id === "good-vibes" ? { ...entry, rowPath: GOOD_VIBES_COLLECTIONS_PATH } : entry));
    goodVibesCollectionsCatalogues.set(catalogue, rows);

    return rows;
}

export function withoutCompensation(catalogue: readonly FeatureCatalogueEntry[]): readonly FeatureCatalogueEntry[] {
    return catalogue.filter(entry => entry.id !== "compensation");
}
